import { useEffect } from 'react';

interface SEOSchemaProps {
  type: 'homepage' | 'article';
  articleData?: {
    title: string;
    description: string;
    datePublished: string;
    dateModified: string;
    authorName: string;
  };
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://cssspreadsheet.website';

const faqItems = [
  { q: 'What is CSSBuy Spreadsheet?', a: 'CSSBuy Spreadsheet is a curated, community-maintained digital collection of verified product links organized in spreadsheet format specifically for CSSBuy users. It contains thousands of product listings from trusted Chinese sellers, complete with direct purchase links, QC reference photos, seller ratings, and pricing information across categories like sneakers, clothing, accessories, and luxury items.' },
  { q: 'Is CSSBuy Spreadsheet free?', a: 'Yes, the CSSBuy Spreadsheet is completely free to access. There are no hidden fees, subscription charges, or premium tiers. We believe in making quality product discovery accessible to everyone in the CSSBuy community. Simply visit cssspreadsheet.website and start browsing immediately.' },
  { q: 'How often is the spreadsheet updated?', a: 'Our spreadsheet is updated daily. New product listings are added as they are discovered and verified by our community contributors. Outdated or dead links are removed promptly to maintain data freshness. We also perform weekly comprehensive audits to ensure all information remains accurate and reliable.' },
  { q: 'Can beginners use CSSBuy Spreadsheet?', a: 'Absolutely. The spreadsheet is designed with beginners in mind. It includes a dedicated getting-started section with step-by-step guides, terminology explanations, and shopping tips. Whether you are making your first CSSBuy purchase or your hundredth, you will find the spreadsheet intuitive and helpful.' },
  { q: 'How do I check QC photos?', a: 'Quality Control photos are linked directly within the spreadsheet for most listings. When you open a product entry, look for the QC gallery link. These are real warehouse photographs taken by CSSBuy inspectors showing the actual product from multiple angles. Review them carefully before confirming your order.' },
  { q: 'How do I choose a reliable seller?', a: 'Look for sellers with the Verified badge in the spreadsheet. These sellers have been vetted through community feedback across multiple transactions. Check the seller rating score, read recent buyer comments, and pay attention to metrics like response time and order fulfillment rate. Sellers with 100+ positive reviews are generally the safest choice.' },
  { q: 'What brands are available in the spreadsheet?', a: 'Our spreadsheet covers a wide range of brands from streetwear to luxury. Popular categories include Nike, Jordan, Adidas, New Balance, Yeezy, Gucci, LV, Dior, Balenciaga, Prada, Chrome Hearts, Stone Island, Essentials, Supreme, and Bape. We also include niche and emerging brands that our community finds interesting.' },
  { q: 'Is international shipping safe?', a: 'Yes, international shipping through CSSBuy is generally safe when you follow best practices. Choose reputable shipping lines like GD-EMS or HK-UPS, purchase shipping insurance for valuable orders, and familiarize yourself with your country customs regulations. Our spreadsheet includes a detailed shipping guide with region-specific recommendations.' },
  { q: 'How do I avoid scams?', a: 'Always use the verified links directly from our spreadsheet. Never pay outside the CSSBuy platform, avoid any deal that seems too good to be true, and be cautious of unsolicited messages from unknown sellers. Stick to Verified sellers, check QC photos carefully, and report suspicious activity to our community moderators.' },
  { q: 'What payment methods work with CSSBuy?', a: 'CSSBuy supports multiple secure payment methods including major credit cards, PayPal, Alipay, WeChat Pay, and various regional payment options depending on your location. All transactions are processed through the platform secure payment gateway, providing buyer protection and easy dispute resolution if needed.' },
  { q: 'How do I find specific products quickly?', a: 'The spreadsheet includes powerful filtering and search features. You can browse by category, filter by brand, sort by price or rating, and use keyword search to locate specific items. Bookmark frequently visited sections for even faster access on return visits.' },
  { q: 'What is the difference between CSSBuy Spreadsheet and manual searching?', a: 'Manual searching requires browsing individual seller pages, forum threads, and scattered social media posts taking hours to find a few good products. The CSSBuy Spreadsheet consolidates everything in one organized location, saving you significant time while providing more comprehensive results with verified quality information.' },
  { q: 'Can I contribute to the spreadsheet?', a: 'Yes! Community contributions are what make the spreadsheet great. If you discover a great product or seller that is not yet listed, you can submit it through our contribution form. Our editorial team reviews all submissions for accuracy and quality before adding them to the spreadsheet.' },
  { q: 'How accurate are the prices in the spreadsheet?', a: 'Prices are updated regularly to reflect current seller listings. However, prices can fluctuate based on demand, batch availability, and seller adjustments. We recommend checking the actual product page for the most up-to-date pricing before placing an order. The spreadsheet prices serve as a reliable estimate.' },
  { q: 'What should I do if a link is broken?', a: 'If you encounter a broken or dead link, please report it through our feedback system. Our team reviews reports daily and either updates the link or removes the listing. We appreciate community help in keeping the spreadsheet accurate and functional for everyone.' },
  { q: 'Is there a mobile version of the spreadsheet?', a: 'Yes, cssspreadsheet.website is fully responsive and works seamlessly on smartphones and tablets. The mobile interface is optimized for touch navigation, making it easy to browse categories, compare products, and access QC photos on the go.' },
  { q: 'How do shipping costs work?', a: 'Shipping costs vary based on package weight, destination country, and chosen shipping line. The spreadsheet includes a shipping calculator reference section with estimated costs for common destinations. Generally, shipping to the US ranges from 15 to 50 USD depending on order size and speed preference.' },
  { q: 'What is the return policy?', a: 'Returns are handled through CSSBuy based on their platform policies. If you receive an item that does not match QC photos or has defects not visible in QC images, you may be eligible for a return or partial refund. Always document issues with clear photos and contact support promptly.' },
  { q: 'Can I use the spreadsheet for bulk orders?', a: 'Yes, the spreadsheet is excellent for bulk ordering research. You can compare prices across multiple sellers for the same product type, identify sellers with bulk discounts, and plan large hauls efficiently. Some sellers offer volume pricing that can significantly reduce per-unit costs.' },
  { q: 'How do I stay updated on new additions?', a: 'Subscribe to our newsletter on this page to receive weekly updates about new spreadsheet additions, trending products, and shopping tips. You can also bookmark cssspreadsheet.website and check back regularly, as new content is added daily.' },
];

export default function SEOSchema({ type, articleData }: SEOSchemaProps) {
  useEffect(() => {
    let schemaObjects: object[] = [];

    if (type === 'homepage') {
      schemaObjects = [
        {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'CSSBuy Spreadsheet',
          url: SITE_URL,
          description: 'The ultimate free CSSBuy Spreadsheet collection with thousands of verified products, QC photos, trusted sellers, and daily updates.',
          potentialAction: {
            '@type': 'SearchAction',
            target: `${SITE_URL}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'CSSBuy Spreadsheet',
          url: SITE_URL,
          description: 'CSSBuy Spreadsheet is the ultimate free resource for discovering verified products, trusted sellers, and QC photos through CSSBuy.',
          sameAs: [
            'https://kaspreadsheet.com',
          ],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.a,
            },
          })),
        },
      ];
    } else if (type === 'article' && articleData) {
      schemaObjects = [
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: articleData.title,
          description: articleData.description,
          datePublished: articleData.datePublished,
          dateModified: articleData.dateModified,
          author: {
            '@type': 'Person',
            name: articleData.authorName,
          },
          publisher: {
            '@type': 'Organization',
            name: 'CSSBuy Spreadsheet',
            url: SITE_URL,
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${SITE_URL}/articles/cssbuy-spreadsheet-ultimate-guide`,
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: SITE_URL,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Articles',
              item: `${SITE_URL}/articles/cssbuy-spreadsheet-ultimate-guide`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: 'CSSBuy Spreadsheet Ultimate Guide',
            },
          ],
        },
      ];
    } else {
      return;
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'readdy-seo-schema';
    script.text = JSON.stringify(
      schemaObjects.length === 1
        ? schemaObjects[0]
        : { '@context': 'https://schema.org', '@graph': schemaObjects }
    );

    const existing = document.getElementById('readdy-seo-schema');
    if (existing) existing.remove();
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('readdy-seo-schema');
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    };
  }, [type, articleData]);

  return null;
}